/**
 * 共享设置面板 — 套餐订阅（Shopify Billing）+ BYO AI Provider 配置
 * 用法: <SettingsPanel api={{ get, post, put }} />
 */
import React, { useEffect, useState, useCallback } from 'react';
import { t } from './i18n';

interface ApiClient {
  get: <T = any>(path: string) => Promise<T>;
  post: <T = any>(path: string, body?: any) => Promise<T>;
  put: <T = any>(path: string, body?: any) => Promise<T>;
}

const S = {
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
  label: { display: 'block', fontSize: 12, color: '#666', marginBottom: 4 } as React.CSSProperties,
};

// ═══ 套餐订阅 ═══
function BillingCard({ api }: { api: ApiClient }) {
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    api.get('/api/v1/billing/plans').then((r: any) => setData(r.data)).catch(() => {});
  }, [api]);
  useEffect(load, [load]);

  const subscribe = async (tier: string) => {
    setBusy(tier);
    try {
      const r: any = await api.post('/api/v1/billing/subscribe', { tier });
      if (r.data?.confirmation_url) {
        // Shopify 付费确认页必须顶层跳转（跳出 iframe）
        if (window.top) window.top.location.href = r.data.confirmation_url;
        else window.location.href = r.data.confirmation_url;
      } else if (r.data?.activated) {
        load();
      }
    } catch (e: any) { alert(e.message); }
    setBusy('');
  };

  if (!data) return <div style={S.card}>{t('加载中...')}</div>;

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>{t('套餐与订阅')}</h3>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
        {t('当前套餐')}: <strong style={{ color: '#1a73e8' }}>{data.current_tier}</strong>
        {data.byok_discount_active && <span style={{ marginLeft: 10, color: '#188038' }}>✓ {t('自带 AI Key 享 7 折')}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {data.plans.map((p: any) => {
          const isCurrent = p.tier === data.current_tier;
          return (
            <div key={p.tier} style={{
              border: isCurrent ? '2px solid #1a73e8' : '1px solid #eee',
              borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
                ${p.effective_price}<span style={{ fontSize: 12, color: '#888' }}>/{t('月')}</span>
                {p.effective_price < p.price && (
                  <span style={{ fontSize: 12, color: '#999', textDecoration: 'line-through', marginLeft: 6 }}>${p.price}</span>
                )}
              </div>
              <ul style={{ fontSize: 12, color: '#555', paddingLeft: 16, marginBottom: 12, minHeight: 54 }}>
                {(p.features || []).map((f: string, i: number) => <li key={i}>{t(f)}</li>)}
              </ul>
              {isCurrent
                ? <div style={{ fontSize: 12, color: '#188038', fontWeight: 600 }}>✓ {t('当前套餐')}</div>
                : <button style={S.btnPrimary} disabled={busy === p.tier} onClick={() => subscribe(p.tier)}>
                    {busy === p.tier ? '...' : t('订阅')}
                  </button>}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginTop: 10 }}>{t('免费试用 7 天，随时取消')}</div>
    </div>
  );
}

// ═══ BYO AI Provider ═══
const PRESETS: Record<string, { base_url: string; model: string }> = {
  openai: { base_url: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  deepseek: { base_url: 'https://api.deepseek.com/v1', model: 'deepseek-chat' },
  moonshot: { base_url: 'https://api.moonshot.cn/v1', model: 'moonshot-v1-8k' },
  qwen: { base_url: 'https://dashscope.aliyuncs.com/compatible-mode/v1', model: 'qwen-plus' },
  custom: { base_url: '', model: '' },
};

function AiProviderCard({ api }: { api: ApiClient }) {
  const [cfg, setCfg] = useState<any>({ mode: 'platform', provider: 'openai', base_url: '', api_key: '', model: '' });
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/v1/settings').then((r: any) => {
      const ai = JSON.parse(r.data?.config_json || '{}').ai_provider;
      if (ai) setCfg({ provider: 'custom', ...ai });
    }).catch(() => {});
  }, [api]);

  const pickPreset = (p: string) => {
    const preset = PRESETS[p] || PRESETS.custom;
    setCfg({ ...cfg, provider: p, base_url: preset.base_url || cfg.base_url, model: preset.model || cfg.model });
  };

  const save = async () => {
    setBusy(true);
    try {
      const cur: any = await api.get('/api/v1/settings');
      const config = JSON.parse(cur.data?.config_json || '{}');
      config.ai_provider = { mode: cfg.mode, base_url: cfg.base_url, api_key: cfg.api_key, model: cfg.model };
      await api.put('/api/v1/settings', { plan_tier: cur.data?.plan_tier, config });
      alert(t('已保存'));
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const r: any = await api.post('/api/v1/billing/test-llm', { base_url: cfg.base_url, api_key: cfg.api_key, model: cfg.model });
      setTestResult(r.success ? `✅ ${t('连接成功')}` : `❌ ${t('连接失败')}`);
    } catch (e: any) {
      setTestResult(`❌ ${t('连接失败')}: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 4 }}>{t('AI 服务配置')}</h3>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>{t('自带 AI Key 享 7 折')}</div>

      <div style={{ marginBottom: 12 }}>
        {([
          ['platform', '使用平台内置 AI（默认）'],
          ['fallback', '自有 AI 作为备用（平台失败时启用）'],
          ['primary', '优先使用自有 AI'],
        ] as const).map(([mode, label]) => (
          <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6, cursor: 'pointer' }}>
            <input type="radio" checked={cfg.mode === mode} onChange={() => setCfg({ ...cfg, mode })} />
            {t(label)}
          </label>
        ))}
      </div>

      {cfg.mode !== 'platform' && (
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {Object.keys(PRESETS).map(p => (
              <button key={p} style={{ ...S.btn, ...(cfg.provider === p ? { borderColor: '#1a73e8', color: '#1a73e8' } : {}) }} onClick={() => pickPreset(p)}>
                {p === 'custom' ? 'Custom' : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>{t('接口地址（OpenAI 兼容）')}</label>
            <input style={S.input} value={cfg.base_url} onChange={e => setCfg({ ...cfg, base_url: e.target.value })} placeholder="https://api.openai.com/v1" />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>API Key</label>
            <input style={S.input} type="password" value={cfg.api_key} onChange={e => setCfg({ ...cfg, api_key: e.target.value })} placeholder="sk-..." />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>{t('模型')}</label>
            <input style={S.input} value={cfg.model} onChange={e => setCfg({ ...cfg, model: e.target.value })} placeholder="gpt-4o-mini" />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button style={S.btnPrimary} disabled={busy} onClick={save}>{busy ? t('保存中...') : t('保存')}</button>
        {cfg.mode !== 'platform' && cfg.api_key && (
          <button style={S.btn} disabled={busy} onClick={test}>{t('测试连接')}</button>
        )}
        {testResult && <span style={{ fontSize: 12 }}>{testResult}</span>}
      </div>
    </div>
  );
}

export function SettingsPanel({ api }: { api: ApiClient }) {
  return (
    <>
      <BillingCard api={api} />
      <AiProviderCard api={api} />
    </>
  );
}
